import React, { useEffect, useState, useCallback } from "react";
import { FaCheckCircle, FaTimesCircle, FaCalendarAlt, FaTrophy, FaChevronDown, FaChevronUp } from "react-icons/fa";
import SummaryApi from "../common";
import { useAuth } from "../helpers/AuthContext";
import announcementImage from "../asset/noexams.png";

const StudentExam = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const { studentId } = useAuth();
  const [openCourse, setOpenCourse] = useState(null); // Track which course is open

  const fetchExamResults = useCallback(async () => {
    if (!studentId) {
      console.log("No student_id in context.");
      setResults([]);
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(SummaryApi.student_exams.url, {
        method: SummaryApi.student_exams.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ student_id: studentId }),
      });

      const data = await res.json();
      if (data.status && Array.isArray(data.data)) {
        setResults(data.data);
      } else {
        setResults([]);
      }
    } catch (error) {
      console.error("Error fetching exam results:", error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    fetchExamResults();
  }, [fetchExamResults]);

  // Group results by course_id
  const groupResultsByCourse = () => {
    const grouped = {};
    results.forEach((result) => {
      const courseId = result.course_id;
      if (!grouped[courseId]) {
        grouped[courseId] = [];
      }
      grouped[courseId].push(result);
    });
    return grouped;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "Invalid";
    }
  };

  // Calculate total possible marks based on number_of_questions
  const getTotalPossibleMarks = (totalMarks, attemptedCount, totalQuestions) => {
    if (!totalQuestions || totalQuestions <= 0 || attemptedCount <= 0) return totalQuestions * 10; // fallback
    const marksPerQuestion = totalMarks / attemptedCount;
    return Math.round(marksPerQuestion * totalQuestions);
  };

  const groupedResults = groupResultsByCourse();

  // Sort courses by latest updated/created date
  const sortedCourseEntries = Object.entries(groupedResults).sort(([, a], [, b]) => {
    const dateA = a[0].updated_date || a[0].created_date;
    const dateB = b[0].updated_date || b[0].created_date;
    return new Date(dateB) - new Date(dateA);
  });

  // Default open: the most recent course (must be before any return)
  useEffect(() => {
    if (sortedCourseEntries.length > 0) {
      const latestCourseId = sortedCourseEntries[0][0];
      setOpenCourse(latestCourseId);
    }
  }, [sortedCourseEntries]);

  if (loading) {
    return (
      <div className="p-6 min-h-screen flex items-center justify-center  from-blue-50 to-indigo-50 dark:from-gray-800">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#1f4774] dark:border-blue-500 border-t-transparent mx-auto"></div>
          <p className="mt-3 text-gray-600 dark:text-gray-300">Loading exam results...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 min-h-screen  from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900">
      <div className="max-w-7xl mx-auto">

        {/* Page Title */}


        {/* No Results */}
        {results.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-screen  from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900">
                    <h1 className="text-2xl sm:text-3xl font-bold text-[#1f4774] dark:text-white mb-6 text-center">
          My Exams
        </h1>
            <img
              src={announcementImage}
              alt="No Exams"
              className="w-48 sm:w-64 h-auto object-contain mb-6 opacity-80"
            />
          </div>
        ) : (
          /* Course-wise Full Width Collapsible Cards */
          <div className="space-y-6">
            {sortedCourseEntries.map(([courseId, courseResults]) => {
              const courseTitle = courseResults[0].course_title || `Course #${courseId}`;
              const isOpen = openCourse === courseId;

              return (
                <div key={courseId} className=" from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900 rounded-xl shadow-md border border-gray-200 overflow-hidden">
                  {/* Course Header - Full Width with Arrow */}
                          <h1 className="text-2xl sm:text-3xl font-bold text-[#1f4774] dark:text-white mb-6 text-center">
          My Exams
        </h1>
                  <div
                    className="bg-gradient-to-r from-[#1f4774] to-[#2a5d8a] dark:from-blue-700 dark:to-blue-900 text-white p-4 flex items-center justify-between cursor-pointer"
                    onClick={() => setOpenCourse(isOpen ? null : courseId)}
                  >
                    <div className="flex items-center gap-2">
                      <FaTrophy className="text-yellow-300 dark:text-yellow-400" />
                      <h2 className="text-xl font-bold">
                        {courseTitle}
                      </h2>
                      <span className="text-sm font-medium bg-white/20 dark:bg-white/10 px-3 py-1 rounded-full ml-2">
                        {courseResults.length} Exam{courseResults.length > 1 ? "s" : ""}
                      </span>
                    </div>
                    <div>
                      {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                    </div>
                  </div>

                  {/* Collapsible Content */}
                  {isOpen && (
                    <div className="p-5 space-y-4">
                      {courseResults.map((result) => {
                        const attemptedCount = Array.isArray(result.question_ids) ? result.question_ids.length : 0;
                        const totalQuestions = result.number_of_questions;
                        const totalPossible = getTotalPossibleMarks(result.total_marks, attemptedCount, totalQuestions);

                        const attemptedText = attemptedCount === 1 
                          ? "1 question attempted" 
                          : `${attemptedCount} questions attempted`;

                        return (
                          <div
                            key={result.result_id}
                            className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-lg p-5 border border-blue-100 dark:border-blue-800 hover:shadow-md transition-shadow"
                          >
                            {/* Assignment Name: value */}
                            <div className="mb-2">
                              <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                                <span className="text-[#1f4774] dark:text-blue-400">Assignment Name:</span> {result.assignment_name}
                              </p>
                            </div>

                            {/* Attempted Count */}
                            <div className="mb-2">
                              <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                                {attemptedText} out of {totalQuestions}
                              </p>
                            </div>

                            {/* Marks */}
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Attempted Marks</span>
                                <span className="font-bold text-gray-800 dark:text-white">
                                  {result.total_marks} / {totalPossible}
                                </span>
                              </div>
                            </div>

                            {/* Date */}
                            <div className="flex items-center gap-2 mt-4 pt-3 border-t border-blue-100 dark:border-blue-800 text-md text-gray-600 dark:text-gray-400">
                              <FaCalendarAlt />
                              <span>Result on: {formatDate(result.updated_date || result.created_date)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentExam;